import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Globe, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const About = () => {
  const features = [
    {
      icon: Shield,
      title: "Trusted Partner",
      description: "Building long-term relationships through reliability and excellence"
    },
    {
      icon: Globe,
      title: "Global Reach",
      description: "Serving clients worldwide with localized understanding"
    },
    {
      icon: Zap,
      title: "Innovation First",
      description: "Staying ahead with cutting-edge technologies and solutions"
    }
  ];

  return (
    <section 
      className="relative py-24 sm:py-32 overflow-hidden"
      data-testid="about-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-600/5 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            className="relative order-2 lg:order-1"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="GWIT Team"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-transparent" />
              </div>

              {/* Floating Card */}
              <motion.div
                className="absolute -bottom-6 -right-6 glass rounded-xl p-6 max-w-[240px]"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="text-4xl font-bold text-blue-400">5+</div>
                <div className="text-sm text-slate-400 mt-1">Years of Excellence in IT Solutions</div>
              </motion.div>

              {/* Decorative Glow */}
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-600/20 rounded-full blur-3xl" />
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-blue-400 mb-4 block">
              About Us
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
              We Build Digital Excellence
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              <strong>GWIT</strong> (Great World Infotech Private Limited / GREATWORLDINFOTECHPRIVATELIMITED) is a next-generation tech-based IT solutions provider delivering scalable, secure, and innovative digital solutions worldwide. As a leading IT company in Bangalore, we are committed to transforming businesses through cutting-edge technology, app development, web development, and blockchain solutions.
            </p>

            {/* CEO Info */}
            <div className="glass rounded-xl p-4 mb-8 inline-flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                RR
              </div>
              <div>
                <div className="text-white font-semibold">Mr. Raghu R</div>
                <div className="text-sm text-slate-400">Founder & CEO</div>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{feature.title}</h4>
                    <p className="text-sm text-slate-400">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Link to="/about">
              <Button
                className="bg-transparent text-white border border-white/20 rounded-full px-6 py-5 font-semibold hover:bg-white/5 transition-all group"
                data-testid="about-learn-more-btn"
              >
                Learn More About Us
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
